import { z } from 'zod';

export const customerFormSchema = z
  .object({
    // Required fields
    sellerId: z
      .string({
        required_error: 'Nhân viên phụ trách là bắt buộc',
      })
      .min(1, 'Vui lòng chọn nhân viên phụ trách'),
    fullName: z
      .string()
      .min(1, 'Vui lòng nhập tên khách hàng')
      .refine(
        (val) => val.trim().length > 0,
        'Tên khách hàng không được chỉ chứa khoảng trắng'
      ),
    dateOfBirth: z
      .date({
        required_error: 'Ngày sinh là bắt buộc',
        invalid_type_error: 'Ngày sinh không hợp lệ',
      })
      .min(new Date('1900-01-01'), 'Ngày sinh không hợp lệ'),
    phoneNumber: z
      .string()
      .min(1, 'Số điện thoại là bắt buộc')
      .regex(/^(0[3|5|7|8|9])+([0-9]{8})\b$/, 'Số điện thoại không hợp lệ')
      .refine(
        (val) => val.trim().length > 0,
        'Số điện thoại không được chỉ chứa khoảng trắng'
      ),
    phoneFamily: z.string().optional().or(z.literal('')),
    email: z
      .string()
      .refine(
        (val) =>
          !val ||
          val.trim().length === 0 ||
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
        'Email không hợp lệ'
      )
      .optional(),
    gender: z.string().min(1, 'Vui lòng chọn giới tính'),

    address: z
      .string()
      .min(1, 'Địa chỉ nhà ở là bắt buộc')
      .refine(
        (val) => !val || val.trim().length > 0,
        'Địa chỉ không được chỉ chứa khoảng trắng'
      ),
    workAddress: z
      .string()
      .min(1, 'Địa chỉ làm việc là bắt buộc')
      .refine(
        (val) => !val || val.trim().length > 0,
        'Địa chỉ không được chỉ chứa khoảng trắng'
      ),
    // Optional fields
    hobbies: z
      .string()
      .optional()
      .refine(
        (val) => !val || val.trim().length > 0,
        'Sở thích không được chỉ chứa khoảng trắng'
      ),
    style: z
      .string()
      .optional()
      .refine(
        (val) => !val || val.trim().length > 0,
        'Phong cách không được chỉ chứa khoảng trắng'
      ),
    favoriteColor: z
      .string()
      .optional()
      .refine(
        (val) => !val || val.trim().length > 0,
        'Màu sắc yêu thích không được chỉ chứa khoảng trắng'
      ),
    notes: z
      .string()
      .optional()
      .refine(
        (val) => !val || val.trim().length > 0,
        'Ghi chú không được chỉ chứa khoảng trắng'
      ),
    maritalStatus: z
      .string()
      .optional()
      .refine(
        (val) => !val || val.trim().length > 0,
        'Tình trạng hôn nhân không được chỉ chứa khoảng trắng'
      ),
    spouseName: z.string().optional().or(z.literal('')),
    spouseBirthday: z.date().optional().nullable(),
    specialNotes: z
      .string()
      .optional()
      .refine(
        (val) => !val || val.trim().length > 0,
        'Ghi chú đặc biệt không được chỉ chứa khoảng trắng'
      ),
  })
  .superRefine((data, ctx) => {
    // Check if phoneNumber and phoneFamily are not the same
    if (
      data.phoneFamily &&
      data.phoneFamily.trim() !== '' &&
      data.phoneFamily === data.phoneNumber
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Số điện thoại chính và số điện thoại gia đình không được trùng nhau',
        path: ['phoneFamily'],
      });
    }

    if (data.maritalStatus === 'married') {
      // Validate phoneFamily
      if (!data.phoneFamily || data.phoneFamily.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Số điện thoại vợ/chồng là bắt buộc khi đã kết hôn',
          path: ['phoneFamily'],
        });
      } else if (!/^(0[3|5|7|8|9])+([0-9]{8})\b$/.test(data.phoneFamily)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Số điện thoại vợ/chồng không hợp lệ',
          path: ['phoneFamily'],
        });
      }

      // Validate spouseName
      if (!data.spouseName || data.spouseName.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Tên vợ/chồng là bắt buộc khi đã kết hôn',
          path: ['spouseName'],
        });
      }

      // Validate spouseBirthday
      if (!data.spouseBirthday) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Ngày sinh vợ/chồng là bắt buộc khi đã kết hôn',
          path: ['spouseBirthday'],
        });
      }
    }
  });

export type CustomerFormValues = z.infer<typeof customerFormSchema>;

// Define required fields for easy reference
export const REQUIRED_FIELDS: (keyof CustomerFormValues)[] = [
  'sellerId',
  'fullName',
  'dateOfBirth',
  'phoneNumber',
  'email',
  'gender',
];
